/**
 * THE one source of truth for "what does a lead need to render this template".
 *
 * mergeVars (render.ts) resolves {{first_name}} from fields.first_name first,
 * else the first word of leads.name — and {{name}} from fields.name, else the
 * name column. Before this module, the importer excluded builtins entirely
 * ("email only") while the gate demanded them from the name column only, so
 * the two could contradict each other AND the renderer. Everything now derives
 * from requiredLeadFields(): the importer's expected columns, the Add-lead
 * form, the CSV template download, and the reconciliation gate below.
 *
 * Lives in shared/ so the wizard gate and the e2e harness run the same code.
 * Counting happens on the server: 10k leads cost one count query per token,
 * never a table download into the browser.
 */
import type { SupabaseClient } from '@supabase/supabase-js'
import { BUILTIN_TOKENS, extractTokens } from './render'
import type { Template } from './types'

/** Tokens that fall back to the leads.name column (see mergeVars). */
const NAME_TOKENS = ['name', 'first_name']

export interface RequiredLeadField {
  token: string
  /**
   * Where the token resolves from, in mergeVars precedence order:
   * 'fields.<key>' entries (the jsonb column an import writes), then 'name'
   * (the leads.name column) for the name-ish tokens. A lead satisfies the
   * token when ANY source is non-empty.
   */
  sources: string[]
}

export interface LeadFieldGap {
  token: string
  /** leads on the campaign that would render {{token}} empty */
  missing: number
}

/**
 * The per-lead tokens this template needs every lead to satisfy, with their
 * resolution places. {{email}} is never here — a lead cannot exist without
 * one — and sender/system tokens are answered by the campaign, not the lead.
 */
export function requiredLeadFields(
  template: Pick<Template, 'subject' | 'body_text' | 'body_html'> | null | undefined,
): RequiredLeadField[] {
  if (!template) return []
  const out: RequiredLeadField[] = []
  for (const token of extractTokens(template.subject, template.body_text, template.body_html)) {
    if (NAME_TOKENS.includes(token)) {
      out.push({ token, sources: [`fields.${token}`, 'name'] })
    } else if (!BUILTIN_TOKENS.includes(token)) {
      out.push({ token, sources: [`fields.${token}`] })
    }
  }
  return out
}

/** 'fields.role' -> the PostgREST filter column for it. */
function sourceColumn(source: string): string {
  return source === 'name' ? 'name' : `fields->>${source.slice('fields.'.length)}`
}

/**
 * Server-counted gaps, one count query per required token, in parallel.
 *
 * A lead is missing a token only when EVERY one of its sources is empty —
 * stacked .or() groups AND together in PostgREST, which makes this exactly as
 * permissive as mergeVars by construction. "Empty" means absent key, null, or
 * empty string; a whitespace-only value slips past this filter and is caught
 * by the send-time guard, which trims.
 */
export async function leadFieldGaps(
  db: SupabaseClient,
  campaignId: string,
  template: Pick<Template, 'subject' | 'body_text' | 'body_html'> | null | undefined,
): Promise<LeadFieldGap[]> {
  // Tokens come from the {{\w+}} matcher, so this filter is belt-and-braces —
  // nothing that could break the PostgREST filter string can get through it.
  const fields = requiredLeadFields(template).filter((f) => /^\w+$/.test(f.token))
  if (!fields.length) return []

  const counts = await Promise.all(
    fields.map(async (f) => {
      let q = db
        .from('leads')
        .select('id', { count: 'exact', head: true })
        .eq('campaign_id', campaignId)
      for (const source of f.sources) {
        const col = sourceColumn(source)
        q = q.or(`${col}.is.null,${col}.eq.`)
      }
      const { count, error } = await q
      if (error) throw new Error(error.message)
      return { token: f.token, missing: count ?? 0 }
    }),
  )
  return counts.filter((g) => g.missing > 0)
}

/** One sentence the wizard can refuse with. */
export function describeGaps(gaps: LeadFieldGap[]): string {
  return gaps
    .map((g) => `${g.missing} lead${g.missing === 1 ? ' is' : 's are'} missing {{${g.token}}}`)
    .join(', ')
}
